import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import {
  getBatchPushReadiness,
  batchPushZipSubmissionsToFacebook,
  pauseVerticalCampaigns,
  resumeVerticalCampaigns,
} from '@src/api/actions/dashboard/zipSubmissions';

// One consolidated push per client: the backend collapses every approved
// submission (across territories AND batches) into ONE full-replace push per
// vertical. This modal renders a section per vertical and a single Push button.
//
// Stages:
//   'loading'  — fetching readiness
//   'ready'    — readiness rendered, waiting for user action
//   'pushing'  — POST in flight
//   'result'   — push complete (per-vertical statuses, may include failures)
//   'error'    — fatal load error

const CHECK_LABELS = {
  pushable_zips_exist: {
    pass: 'ZIPs available to push',
    fail: 'No pushable ZIPs',
    help: 'No territories for this vertical have budget this month. Add a budget to push.',
  },
  broad_campaigns_found: {
    pass: 'Broad campaigns found',
    fail: 'No broad campaigns found',
    help: 'No ct=broad campaigns under an active ad account match this vertical.',
  },
};

const REFUSAL_MESSAGES = {
  no_pushable_zips: 'Refused: no pushable ZIPs remained at push time.',
  no_broad_campaigns: 'Refused: no broad campaigns matched at push time.',
  readiness_stale: 'Refused: state changed while the modal was open.',
};

function PreflightCheck({ name, ok }) {
  const label = CHECK_LABELS[name] || {};
  const Icon = ok ? CheckCircleIcon : CancelIcon;
  const color = ok ? 'success.main' : 'error.main';
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Icon sx={{ color, mt: '2px' }} fontSize="small" />
      <Box>
        <Typography variant="body2" fontWeight={500}>
          {ok ? label.pass : label.fail}
        </Typography>
        {!ok && label.help && (
          <Typography variant="caption" color="text.secondary">
            {label.help}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function TerritoryTable({ rows }) {
  return (
    <TableContainer sx={{ maxHeight: 240 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Territory</TableCell>
            <TableCell align="right">ZIPs</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id ?? row.name}>
              <TableCell>{row.name}</TableCell>
              <TableCell align="right">{row.count}</TableCell>
              <TableCell>
                {row.included ? (
                  <Chip size="small" color="success" label="Included" variant="outlined" />
                ) : (
                  <Chip
                    size="small"
                    color="default"
                    label="No budget this month"
                    variant="outlined"
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function FbTargets({ adAccounts }) {
  const [openAccounts, setOpenAccounts] = useState(() => new Set());
  const toggle = (id) => {
    setOpenAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Stack spacing={1}>
      {adAccounts.map((acct) => {
        const totalAdSets = acct.campaigns.reduce((sum, c) => sum + c.ad_sets.length, 0);
        const isOpen = openAccounts.has(acct.id);
        return (
          <Box key={acct.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 1.5, py: 1, cursor: 'pointer' }}
              onClick={() => toggle(acct.id)}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {acct.name || acct.account_id}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {acct.campaigns.length} broad campaign{acct.campaigns.length !== 1 ? 's' : ''} · {totalAdSets} ad set{totalAdSets !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <IconButton size="small">
                {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Stack>
            <Collapse in={isOpen}>
              <Box sx={{ px: 2, pb: 1 }}>
                {acct.campaigns.map((camp) => (
                  <Box key={camp.id} sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {camp.name}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={0.5}>
                      {camp.ad_sets.map((a) => (
                        <Chip
                          key={a.id}
                          size="small"
                          label={a.name || a.ad_set_id}
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Stack>
  );
}

function VerticalResult({ outcome, onRetry, retrying }) {
  if (outcome.status === 'refused') {
    return (
      <Alert severity="error">
        {REFUSAL_MESSAGES[outcome.code] || `Refused (${outcome.code}).`}
      </Alert>
    );
  }
  const result = outcome.result;
  const failed = result.results.filter((r) => !r.success);
  const succeeded = result.results.filter((r) => r.success);
  return (
    <Stack spacing={1.5}>
      <Alert severity={failed.length === 0 ? 'success' : 'warning'}>
        Pushed to {succeeded.length} ad set{succeeded.length !== 1 ? 's' : ''}
        {failed.length > 0 && ` · ${failed.length} failed`}
        {' · '}
        {result.zip_count} ZIPs
      </Alert>
      {failed.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Failed ad sets
          </Typography>
          <Stack spacing={0.5}>
            {failed.map((r) => (
              <Alert
                key={r.ad_set_id}
                severity="error"
                icon={<ErrorOutlineIcon />}
                variant="outlined"
                sx={{ py: 0 }}
              >
                <Typography variant="body2" fontWeight={500}>
                  {r.ad_set_name || r.ad_set_id}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {r.error}
                </Typography>
              </Alert>
            ))}
          </Stack>
          <Button
            sx={{ mt: 1 }}
            size="small"
            variant="outlined"
            onClick={() => onRetry(failed.map((r) => r.ad_set_id))}
            disabled={retrying}
          >
            {retrying ? <CircularProgress size={16} /> : `Retry failed (${failed.length})`}
          </Button>
        </Box>
      )}
    </Stack>
  );
}

function VerticalSection({
  entry,
  outcome,
  defaultExpanded,
  onRetry,
  retrying,
  onPauseCampaigns,
  pausing,
  pauseOutcome,
  onResumeCampaigns,
  resuming,
  resumeOutcome,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const territories = entry.territories_updated || [];
  const campaignCount = entry.fb_targets.ad_accounts.reduce(
    (sum, acct) => sum + acct.campaigns.length,
    0,
  );
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 1.5, py: 1, cursor: 'pointer' }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          {entry.vertical}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          label={`${entry.zip_set.total_pushable} ZIPs`}
        />
        {territories.length > 0 && (
          <Chip
            size="small"
            variant="outlined"
            color="info"
            label={
              territories.length > 2
                ? `${territories.length} territories updated`
                : `updated: ${territories.join(', ')}`
            }
          />
        )}
        {!entry.can_push && (
          <Chip size="small" color="error" variant="outlined" label="cannot push" />
        )}
        {outcome && (
          <Chip
            size="small"
            color={
              outcome.status === 'pushed' ? 'success'
              : outcome.status === 'partial' ? 'warning'
              : 'error'
            }
            label={outcome.status}
          />
        )}
        <Box flexGrow={1} />
        <IconButton size="small">
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Stack>
      <Collapse in={expanded}>
        <Stack spacing={2} sx={{ px: 1.5, pb: 1.5 }}>
          <Stack spacing={1}>
            <PreflightCheck
              name="pushable_zips_exist"
              ok={entry.checks.pushable_zips_exist}
            />
            <PreflightCheck
              name="broad_campaigns_found"
              ok={entry.checks.broad_campaigns_found}
            />
          </Stack>
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={1}>
              <Typography variant="subtitle2">ZIPs to push</Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>{entry.zip_set.total_pushable}</strong> pushable
                {entry.zip_set.filtered_out_count > 0 && (
                  <> · {entry.zip_set.filtered_out_count} excluded by targeting filters</>
                )}
              </Typography>
            </Stack>
            <TerritoryTable rows={entry.zip_set.by_territory} />
          </Box>
          {entry.fb_targets.ad_accounts.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Will replace geo_locations on
              </Typography>
              <FbTargets adAccounts={entry.fb_targets.ad_accounts} />
            </Box>
          )}
          {entry.should_pause_campaigns && !pauseOutcome && (
            <Alert
              severity="warning"
              action={
                <Button
                  color="warning"
                  size="small"
                  variant="outlined"
                  disabled={pausing}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPauseCampaigns();
                  }}
                >
                  {pausing ? (
                    <CircularProgress size={16} />
                  ) : (
                    `Pause ${campaignCount} campaign${campaignCount !== 1 ? 's' : ''}`
                  )}
                </Button>
              }
            >
              No pushable ZIPs remain for this vertical (no budgeted
              territories) — its broad campaigns are still live with stale
              targeting and should be paused.
            </Alert>
          )}
          {pauseOutcome && (
            <Alert severity={pauseOutcome.failed_count === 0 ? 'success' : 'error'}>
              Paused {pauseOutcome.paused_count} campaign{pauseOutcome.paused_count !== 1 ? 's' : ''}
              {pauseOutcome.failed_count > 0 && ` · ${pauseOutcome.failed_count} failed`}
            </Alert>
          )}
          {/* Reactivation is only offered once the vertical is pushable again
              (live budget back) — while it's still turned off, resuming would
              just restart spend the client asked us to stop. */}
          {entry.unresumed_pause && !entry.should_pause_campaigns && !resumeOutcome && (
            <Alert
              severity="info"
              action={
                <Button
                  size="small"
                  variant="outlined"
                  disabled={resuming}
                  onClick={(e) => {
                    e.stopPropagation();
                    onResumeCampaigns();
                  }}
                >
                  {resuming ? (
                    <CircularProgress size={16} />
                  ) : (
                    `Reactivate ${entry.unresumed_pause.campaign_count} campaign${entry.unresumed_pause.campaign_count !== 1 ? 's' : ''}`
                  )}
                </Button>
              }
            >
              {entry.unresumed_pause.campaign_count} campaign
              {entry.unresumed_pause.campaign_count !== 1 ? 's were' : ' was'} paused
              {' '}on {new Date(entry.unresumed_pause.paused_at).toLocaleDateString()}
              {entry.unresumed_pause.trigger === 'budget_turnoff' ? ' after a budget turn-off' : ''}.
              This vertical has live coverage again — only the campaigns paused
              then will be re-enabled.
            </Alert>
          )}
          {resumeOutcome && (
            <Alert severity={resumeOutcome.failed_count === 0 ? 'success' : 'error'}>
              Reactivated {resumeOutcome.resumed_count} campaign{resumeOutcome.resumed_count !== 1 ? 's' : ''}
              {resumeOutcome.failed_count > 0 && ` · ${resumeOutcome.failed_count} failed — retry or check Ads Manager`}
            </Alert>
          )}
          {outcome && (
            <VerticalResult outcome={outcome} onRetry={onRetry} retrying={retrying} />
          )}
        </Stack>
      </Collapse>
    </Box>
  );
}

export default function PushToFacebookModal({
  open,
  onClose,
  clientId,
  verticalIds = null,
  onPushComplete,
}) {
  const [stage, setStage] = useState('loading');
  const [readiness, setReadiness] = useState(null);
  const [error, setError] = useState('');
  // vertical_id -> {status, code?, result?} from the push response
  const [outcomes, setOutcomes] = useState({});
  const [retryingVertical, setRetryingVertical] = useState(null);
  // vertical_id -> {paused_count, failed_count} from pause-campaigns
  const [pauseOutcomes, setPauseOutcomes] = useState({});
  const [pausingVertical, setPausingVertical] = useState(null);
  // vertical_id -> {resumed_count, failed_count} from resume-campaigns
  const [resumeOutcomes, setResumeOutcomes] = useState({});
  const [resumingVertical, setResumingVertical] = useState(null);

  const loadReadiness = useCallback(async () => {
    if (!clientId) return;
    setStage('loading');
    setError('');
    try {
      const res = await getBatchPushReadiness(clientId, verticalIds);
      setReadiness(res.data);
      setStage('ready');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to load push readiness.';
      setError(detail);
      setStage('error');
    }
  }, [clientId, verticalIds]);

  useEffect(() => {
    if (open && clientId) {
      setOutcomes({});
      setPauseOutcomes({});
      setResumeOutcomes({});
      loadReadiness();
    }
  }, [open, clientId, loadReadiness]);

  const entries = readiness?.verticals || [];
  const pushableEntries = entries.filter((e) => e.can_push);

  const handlePush = async () => {
    setStage('pushing');
    setError('');
    try {
      const res = await batchPushZipSubmissionsToFacebook({
        client_id: clientId,
        vertical_ids: pushableEntries.map((e) => e.vertical_id),
      });
      const byVertical = {};
      for (const entry of res.data.verticals) {
        byVertical[entry.vertical_id] = entry;
      }
      setOutcomes(byVertical);
      setStage('result');
      onPushComplete?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Push failed.';
      setError(detail);
      setStage('ready');
    }
  };

  const handleRetry = async (verticalId, adSetIds) => {
    setRetryingVertical(verticalId);
    try {
      const res = await batchPushZipSubmissionsToFacebook({
        client_id: clientId,
        vertical_ids: [verticalId],
        retry_ad_set_ids: adSetIds,
      });
      const fresh = res.data.verticals.find((v) => v.vertical_id === verticalId);
      if (fresh) {
        setOutcomes((prev) => {
          const prior = prev[verticalId];
          if (prior?.result && fresh.result) {
            const priorSuccess = prior.result.results.filter((r) => r.success);
            fresh.result = {
              ...fresh.result,
              results: [...priorSuccess, ...fresh.result.results],
              pushed_count: priorSuccess.length + fresh.result.pushed_count,
            };
            if (fresh.status === 'pushed' && prior.status === 'partial') {
              fresh.status = 'pushed';
            }
          }
          return { ...prev, [verticalId]: fresh };
        });
        onPushComplete?.();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Retry failed.';
      setError(detail);
    } finally {
      setRetryingVertical(null);
    }
  };

  const handlePauseCampaigns = async (verticalId) => {
    setPausingVertical(verticalId);
    setError('');
    try {
      const res = await pauseVerticalCampaigns(clientId, verticalId);
      setPauseOutcomes((prev) => ({ ...prev, [verticalId]: res.data }));
      onPushComplete?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Pause failed.';
      setError(detail);
    } finally {
      setPausingVertical(null);
    }
  };

  const handleResumeCampaigns = async (verticalId) => {
    setResumingVertical(verticalId);
    setError('');
    try {
      const res = await resumeVerticalCampaigns(clientId, verticalId);
      setResumeOutcomes((prev) => ({ ...prev, [verticalId]: res.data }));
      onPushComplete?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Reactivation failed.';
      setError(detail);
    } finally {
      setResumingVertical(null);
    }
  };

  const title = useMemo(() => {
    if (entries.length === 1) {
      return `Push to Facebook — ${entries[0].vertical}`;
    }
    if (entries.length > 1) {
      return `Push to Facebook — ${entries.length} verticals`;
    }
    return 'Push ZIPs to Facebook';
  }, [entries]);

  const totalSubmissionsCovered = entries.reduce(
    (sum, e) => sum + (e.covered_submission_ids?.length || 0),
    0,
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {stage === 'loading' && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {stage === 'error' && (
          <Alert severity="error" sx={{ my: 1 }}>
            {error}
          </Alert>
        )}

        {stage !== 'loading' && stage !== 'error' && entries.length === 0 && (
          <Alert severity="info">
            Nothing awaiting a push — every approved submission for this client
            has already been pushed to Facebook.
          </Alert>
        )}

        {(stage === 'ready' || stage === 'pushing' || stage === 'result') && entries.length > 0 && (
          <Stack spacing={2}>
            {error && stage === 'ready' && (
              <Alert severity="warning">{error}</Alert>
            )}
            {entries.length > 1 && stage !== 'result' && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                One push per vertical replaces targeting for ALL its territories
                at once
                {totalSubmissionsCovered > 0 &&
                  ` — covering ${totalSubmissionsCovered} approved submission${totalSubmissionsCovered !== 1 ? 's' : ''}`}
                . No need to push each submission separately.
              </Alert>
            )}
            {entries.map((entry) => (
              <VerticalSection
                key={entry.vertical_id}
                entry={entry}
                outcome={outcomes[entry.vertical_id]}
                defaultExpanded={entries.length <= 2}
                onRetry={(adSetIds) => handleRetry(entry.vertical_id, adSetIds)}
                retrying={retryingVertical === entry.vertical_id}
                onPauseCampaigns={() => handlePauseCampaigns(entry.vertical_id)}
                pausing={pausingVertical === entry.vertical_id}
                pauseOutcome={pauseOutcomes[entry.vertical_id]}
                onResumeCampaigns={() => handleResumeCampaigns(entry.vertical_id)}
                resuming={resumingVertical === entry.vertical_id}
                resumeOutcome={resumeOutcomes[entry.vertical_id]}
              />
            ))}
            <Divider />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {stage === 'ready' && entries.length > 0 && totalSubmissionsCovered > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', pl: 1 }}>
            Closing is safe — unpushed approvals auto-push within a few minutes.
          </Typography>
        )}
        <Button onClick={onClose}>{stage === 'result' ? 'Close' : 'Cancel'}</Button>
        {stage !== 'result' && entries.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            onClick={handlePush}
            disabled={stage !== 'ready' || pushableEntries.length === 0}
          >
            {stage === 'pushing' ? (
              <CircularProgress size={18} />
            ) : (
              `Push to Facebook${pushableEntries.length > 1 ? ` (${pushableEntries.length} verticals)` : ''}`
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
